import { ldb, state } from '@codesuma/baseline'
import phoneCalendar from './phone-calendar'
import customOccasions from './custom-occasions'
import settings from './settings'
import { ICandidate, candidates } from './event-adoption'
import { MIRROR_CALENDARS } from '../configs/calendars'

// Looks through the phone's calendar once a launch for events that are really
// occasions, and holds what it finds for the day page to mark.
//
// The rule it applies is in services/event-adoption.ts, which is pure. This is
// only the part that has to touch the phone, the clock and the store.
//
// Nothing is adopted on its own. The day page marks a candidate, the user
// decides, and either answer is final: adopting writes the occasion, and
// declining writes the id to the list below so the same event is never offered
// twice.

// Ids the user has said to leave alone. Kept rather than recomputed — a
// decision not to adopt is not visible anywhere else.
const DISMISSED = 'adoption-dismissed'

// Far enough into the launch to be clear of it. The scan is a wide read across
// a year of calendar and there is nothing waiting on the answer.
const DELAY_MS = 2500

// A year back and a year on.
//
// Both halves have to be a year, and that is not obvious. A yearly event is
// recognised by two of its occurrences sitting twelve months apart inside the
// window, so the window has to be wide enough to hold two of them wherever the
// event falls. Look back only a month and the rule quietly stops working for
// anything more than a month away: a birthday six months off has its neighbours
// 345 days on either side, and neither is in view.
const MONTHS_BACK = 13
const MONTHS_ON = 13

// Everything the last scan found, and what is left of it once anything since
// adopted or declined is taken out. The second is what the day page marks from,
// so the marks and the broadcast can never disagree.
let found: ICandidate[] = []
let offered: ICandidate[] = []

function dismissed(): string[] {
    const stored = ldb.get(DISMISSED)
    return Array.isArray(stored) ? stored : []
}

// What is worth offering right now: everything found by the last scan, less
// anything since adopted or declined.
function refresh() {
    const skip = new Set([...dismissed(), ...customOccasions.sources()])
    offered = found.filter(c => !skip.has(c.id))
    state.set('adoption', offered)
}

async function scan() {
    // The scan reads the phone, so it only runs where the user has already said
    // the phone's calendar may be read.
    if (!settings.get().showPhoneEvents) return

    const now = new Date()
    const from = new Date(now.getFullYear(), now.getMonth() - MONTHS_BACK, 1).getTime()
    const to = new Date(now.getFullYear(), now.getMonth() + MONTHS_ON, 1).getTime()

    found = candidates(await phoneCalendar.scanRange(from, to), {
        skipCalendars: MIRROR_CALENDARS,
        skipIds: [...dismissed(), ...customOccasions.sources()],
    })
    refresh()
}

export default {
    // What the day page draws its marks from. Empty until the scan lands, and
    // empty for good on the web.
    all: (): ICandidate[] => offered,

    // The user has said to leave this one as an event. Remembered for good:
    // being asked twice about the same birthday is worse than not being asked.
    keep(id: string) {
        if (dismissed().includes(id)) return
        ldb.set(DISMISSED, [...dismissed(), id])
        refresh()
    },

    init() {
        if (!phoneCalendar.available()) return
        setTimeout(scan, DELAY_MS)
        // An occasion saved or deleted changes what is left to offer, and an
        // adopted one has to stop being offered at once.
        state.on('custom-occasions', refresh)
    },
}
